import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    type PropsWithChildren,
} from "react";

export type Toast = {
    title: string;
    id: string;
    message: string;
    type?: "info" | "success" | "warning" | "error";
    duration?: number; // ms
};

type ToastContextValue = {
    toasts: Toast[];
    addToast: (t: Omit<Toast, "id">) => string;
    removeToast: (id: string) => void;
    clear: () => void;
    showWarning: (title: string, message: string, duration?: number) => void;
    showError: (title: string, message: string, duration?: number) => void;
    showInfo?: (title: string, message: string, duration?: number) => void;
    showSuccess?: (title: string, message: string, duration?: number) => void;
};

const ToastCtx = createContext<ToastContextValue | undefined>(undefined);

// Supported positions for the renderer
type ToastPosition =
    | "top-left"
    | "top-right"
    | "top-center"
    | "bottom-left"
    | "bottom-right"
    | "bottom-center";

interface ToastProviderProps {
    position?: ToastPosition; // default "top-right"
    maxToasts?: number;       // default 5
}

export const ToastProvider: React.FC<PropsWithChildren<ToastProviderProps>> = ({
    children,
    position = "top-right",
    maxToasts = 5,
}) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const removeToast = useCallback((id: string) => {
        setToasts((t) => t.filter((x) => x.id !== id));
    }, []);
  

    const addToast = useCallback(
        (t: Omit<Toast, "id">) => {
            const id =
                globalThis.crypto?.randomUUID?.() ??
                Math.random().toString(36).slice(2);
            const toast: Toast = { id, type: "info", duration: 4000, ...t };
            setToasts((prev) => [...prev, toast]);

            if (toast.duration && toast.duration > 0) {
                // basic auto-dismiss
                window.setTimeout(() => removeToast(id), toast.duration);
            }
            return id;
        },
        [removeToast]
    );



    const showWarning = useCallback(
        (title: string, message: string) => {
            addToast({ title, message, type: "warning", duration: 4000 });
        },
        [addToast]
    );
    const showError = useCallback(
        (title: string, message: string) => {
            addToast({ title, message, type: "error", duration: 4000 });
        },
        [addToast]
    );
    const showSuccess = useCallback(
        (title:string, message: string) => {
            addToast({ title, message, type: "success", duration: 4000 });
        },
        [addToast]
    );
    const showInfo = useCallback(
        (title:string,message: string) => {
            addToast({ title, message, type: "info", duration: 4000 });
        },
        [addToast]
    );

    const clear = useCallback(() => setToasts([]), []);

    // Limit how many are visible at once
    const visibleToasts = maxToasts > 0 ? toasts.slice(-maxToasts) : toasts;

    // Compute container positioning from `position`
    const basePos: React.CSSProperties = {
        position: "fixed",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        zIndex: 9999,
        pointerEvents: "none", // let clicks fall through except on toasts
    };
    const posStyle: React.CSSProperties = (() => {
        const common: React.CSSProperties = {};
        if (position.includes("top")) common.top = 16;
        if (position.includes("bottom")) common.bottom = 16;
        if (position.includes("left")) common.left = 16;
        if (position.includes("right")) common.right = 16;
        if (position.includes("center")) {
            common.left = "50%";
            common.transform = "translateX(-50%)";
        }
        return common;
    })();

    const toastStyle: React.CSSProperties = {
        padding: "8px 12px",
        borderRadius: 6,
        background: "#333",
        color: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,.2)",
        maxWidth: 420,
        pointerEvents: "auto", // allow interaction with toast
    };

    return (
        <ToastCtx.Provider value={{ toasts, addToast, removeToast, clear, showWarning, showError, showSuccess, showInfo }}>
            {children}

            {/* simple renderer so toasts are visible even without UI wiring */}
            <div style={{ ...basePos, ...posStyle }}>
                {visibleToasts.map((t) => (
                    <div key={t.id} role="status" style={toastStyle}>
                        {t.message}
                    </div>
                ))}
            </div>
        </ToastCtx.Provider>
    );
};

export const useToast = (): ToastContextValue => {
    const ctx = useContext(ToastCtx);
    if (!ctx) throw new Error("useToast must be used within a ToastProvider");
    return ctx;
};

export default ToastProvider;
